import { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, X } from 'lucide-react';
import { createWorker } from 'tesseract.js';
import { supabase } from '../lib/supabase';

// Global variable to track active camera streams
const activeStreams = new Set<MediaStream>();

// Function to stop all camera streams
const stopAllCameraStreams = () => {
  console.log('Stopping all camera streams...');
  activeStreams.forEach(stream => {
    try {
      stream.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      stream.removeTrack = () => stream; // Prevent further operations
    } catch (e) {
      console.error('Error stopping stream:', e);
    }
  });
  activeStreams.clear();
  
  // Force clear video elements
  document.querySelectorAll('video').forEach(video => {
    try {
      video.pause();
      video.srcObject = null;
      video.load();
    } catch (e) {
      console.error('Error cleaning video element:', e);
    }
  });
};

interface PhotoCaptureProps {
  onDataExtracted: (data: {
    reference_number?: string;
    claim_number?: string;
    not_at_fault_name?: string;
    not_at_fault_policy_number?: string;
    at_fault_name?: string;
    at_fault_policy_number?: string;
  }) => void;
  onClose: () => void;
}

export function PhotoCapture({ onDataExtracted, onClose }: PhotoCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopCamera = useCallback(() => {
    console.log('Stopping camera...');
    
    // Stop all camera streams globally first
    stopAllCameraStreams();
    
    // Get current stream from video element
    const videoElement = videoRef.current;
    const currentStream = videoElement?.srcObject as MediaStream;
    
    if (currentStream) {
      try {
        console.log('Stopping current stream tracks');
        const tracks = currentStream.getTracks();
        
        // Stop all tracks immediately
        tracks.forEach(track => {
          console.log(`Stopping ${track.kind} track`);
          try {
            track.stop();
            track.enabled = false;
            track.dispatchEvent(new Event('ended'));
          } catch (e) {
            console.error('Error stopping track:', e);
          }
        });
        
        // Clear the stream
        currentStream.removeTrack = () => currentStream;
        
        // Remove from active streams
        activeStreams.delete(currentStream);
      } catch (e) {
        console.error('Error in stopCamera:', e);
      }
    }
    
    // Clear the video element
    if (videoElement) {
      try {
        videoElement.pause();
        videoElement.srcObject = null;
        videoElement.load();
        videoElement.src = '';
      } catch (e) {
        console.error('Error cleaning video element:', e);
      }
    }
    
    // Clear the stream state
    setStream(null);
    
    // Force garbage collection if available
    if (window.gc) {
      window.gc();
    }
    
    console.log('Camera stopped');
  }, []);

  // Handle camera initialization and cleanup
  useEffect(() => {
    let isMounted = true;
    
    const handleBackButton = () => {
      console.log('Back button pressed, stopping camera...');
      stopCamera();
    };
    
    // Add event listener for popstate (triggered by back/forward buttons)
    window.addEventListener('popstate', handleBackButton);
    
    // Start the camera
    const initCamera = async () => {
      try {
        console.log('Initializing camera...');
        await startCamera();
      } catch (err) {
        console.error('Failed to start camera:', err);
      }
    };
    
    if (isMounted) {
      initCamera();
    }
    
    // Cleanup function
    return () => {
      console.log('Cleaning up camera component...');
      isMounted = false;
      stopCamera();
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [stopCamera]); // Add stopCamera to dependencies

  const startCamera = async () => {
    // Stop any existing streams first
    stopAllCameraStreams();
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });
      
      // Track this stream
      activeStreams.add(mediaStream);
      
      // Only proceed if component is still mounted
      if (!videoRef.current) {
        stopAllCameraStreams();
        return;
      }
      
      // Set the stream to state and video element
      setStream(mediaStream);
      const videoElement = videoRef.current;
      videoElement.srcObject = mediaStream;
      
      // Wait for the video to be ready
      return new Promise<void>((resolve) => {
        const onLoaded = () => {
          videoElement.play().then(resolve).catch(console.error);
        };
        
        if (videoElement.readyState >= 2) { // HAVE_CURRENT_DATA
          onLoaded();
        } else {
          videoElement.onloadedmetadata = onLoaded;
        }
      });
    } catch (err) {
      setError('Could not access camera. Please ensure camera permissions are granted.');
      console.error('Camera access error:', err);
      throw err;
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setProcessing(true);
    setError(null);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (!context) throw new Error('Could not get canvas context');

      // Set canvas size to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Capture frame from video
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve) => 
        canvas.toBlob(blob => resolve(blob!), 'image/jpeg', 0.95)
      );

      // Store image in Supabase Storage
      const fileName = `${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('case-files')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      // Process with Tesseract
      const worker = await createWorker('eng');
      try {
        const { data: { text } } = await worker.recognize(blob);
        
        // Extract data from OCR text
        const extractedData = {
          reference_number: extractReferenceNumber(text),
          claim_number: extractClaimNumber(text),
          not_at_fault_name: extractName(text, 'not_at_fault'),
          not_at_fault_policy_number: extractPolicyNumber(text, 'not_at_fault'),
          at_fault_name: extractName(text, 'at_fault'),
          at_fault_policy_number: extractPolicyNumber(text, 'at_fault')
        };

        onDataExtracted(extractedData);
      } finally {
        await worker.terminate();
      }
    } catch (err) {
      setError('Error processing image. Please try again.');
      console.error('Processing error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const extractReferenceNumber = (text: string): string | undefined => {
    const match = text.match(/ref(?:erence)?[\s#.:]+([A-Z0-9-]+)/i);
    return match?.[1];
  };

  const extractClaimNumber = (text: string): string | undefined => {
    const match = text.match(/claim[\s#.:]+([A-Z0-9-]+)/i);
    return match?.[1];
  };

  const extractName = (text: string, type: 'not_at_fault' | 'at_fault'): string | undefined => {
    const regex = type === 'not_at_fault'
      ? /(?:claimant|not[\s-]at[\s-]fault)(?:'s)?\s*name[\s.:]+([A-Za-z\s]+)/i
      : /(?:at[\s-]fault|third[\s-]party)(?:\s*driver|\s*party)?\s*name[\s.:]+([A-Za-z\s]+)/i;
    const match = text.match(regex);
    return match?.[1]?.trim();
  };

  const extractPolicyNumber = (text: string, type: 'not_at_fault' | 'at_fault'): string | undefined => {
    const regex = type === 'not_at_fault'
      ? /(?:claimant|not[\s-]at[\s-]fault)(?:'s)?\s*policy[\s#.:]+([A-Z0-9-]+)/i
      : /(?:at[\s-]fault|third[\s-]party)\s*policy[\s#.:]+([A-Z0-9-]+)/i;
    const match = text.match(regex);
    return match?.[1];
  };

  const handleClose = useCallback(() => {
    console.log('handleClose called');
    
    // Get the current stream from the video element
    const videoElement = videoRef.current;
    const currentStream = videoElement?.srcObject as MediaStream;
    
    // Stop all tracks immediately
    if (currentStream) {
      console.log('Stopping all media tracks');
      const tracks = currentStream.getTracks();
      tracks.forEach(track => {
        console.log(`Stopping ${track.kind} track`);
        track.stop();
        track.enabled = false;
      });
      
      // Clear the stream
      currentStream.removeTrack = () => currentStream; // Prevent any further operations
      
      // Clear the video element
      if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
        videoElement.load(); // Force reload
      }
      
      // Clear the stream state
      setStream(null);
    }
    
    // Call the parent's onClose immediately
    onClose();
    
    // Force garbage collection if possible
    if (window.gc) {
      window.gc();
    }
    
    // Prevent back button from going back to camera
    window.history.replaceState(null, '', window.location.pathname);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {processing ? 'Processing Document...' : 'Capture Document'}
          </h2>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={processing}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
          <button
            onClick={capturePhoto}
            disabled={processing || !stream}
            className="absolute top-4 left-1/2 -translate-x-1/2 z-10 w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed border-4 border-white shadow-lg transform transition-transform active:scale-95 flex items-center justify-center"
            aria-label="Take photo"
          >
            <Camera className="h-8 w-8 text-white" />
          </button>

          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-contain"
          />
          <canvas
            ref={canvasRef}
            className="hidden"
          />
          
          {processing && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-2"></div>
                <p>Processing document...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}